import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const NewsletterSignup = () => {
  const [email, setEmail] = useState('');
  const [preferences, setPreferences] = useState({
    deals: true,
    reviews: false,
    guides: false
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setIsLoading(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setIsLoading(false);
    setIsSubmitted(true);
  };

  const handlePreferenceChange = (key) => {
    setPreferences(prev => ({
      ...prev,
      [key]: !prev?.[key]
    }));
  };

  const benefits = [
    {
      icon: 'Tag',
      title: 'Exclusive Deals',
      description: 'Get notified about price drops and limited-time offers before anyone else.'
    },
    {
      icon: 'Star',
      title: 'New Reviews',
      description: 'Be the first to read our latest vacuum reviews and comparisons.'
    },
    {
      icon: 'BookOpen',
      title: 'Expert Tips',
      description: 'Receive maintenance guides and cleaning tips from our experts.'
    },
    {
      icon: 'Zap',
      title: 'Personalized Picks',
      description: 'Get vacuum recommendations tailored to your home and needs.'
    }
  ];

  if (isSubmitted) {
    return (
      <section className="bg-gradient-to-br from-primary to-blue-900 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-2xl mx-auto">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-6">
              <Icon name="CheckCircle" size={32} className="text-green-600" />
            </div>
            
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Welcome to Vacuum Guide!
            </h2>
            
            <p className="text-lg text-gray-600 mb-6">
              Thanks for subscribing! Check your email for a welcome message with your first exclusive deal.
            </p>
            
            <div className="bg-blue-50 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-center space-x-2 text-primary">
                <Icon name="Gift" size={20} />
                <span className="font-semibold">
                  Welcome Bonus: 15% off your first purchase!
                </span>
              </div>
            </div>
            
            <Button
              variant="default"
              size="lg"
              iconName="ArrowRight"
              iconPosition="right"
              onClick={() => {
                setIsSubmitted(false);
                setEmail('');
                setPreferences({ deals: true, reviews: false, guides: false });
              }}
            >
              Continue Browsing
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-gradient-to-br from-primary to-blue-900 py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content Side */}
          <div className="text-white">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">
              Never Miss a Great Deal
            </h2>
            <p className="text-xl text-blue-100 mb-8 leading-relaxed">
              Join 500,000+ smart shoppers who get exclusive access to vacuum deals, expert reviews, and personalized recommendations.
            </p>

            {/* Benefits Grid */}
            <div className="grid sm:grid-cols-2 gap-6">
              {benefits?.map((benefit, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
                    <Icon name={benefit?.icon} size={20} className="text-accent" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">
                      {benefit?.title}
                    </h3>
                    <p className="text-sm text-blue-100">
                      {benefit?.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Form Side */}
          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                Get Started Today
              </h3>
              <p className="text-gray-600">
                Choose what you'd like to receive and we'll keep you updated.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email Input */}
              <Input
                type="email"
                label="Email Address"
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e?.target?.value)}
                required
                className="mb-4"
              />

              {/* Preferences */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-3">
                  What would you like to receive?
                </label>
                <div className="space-y-3">
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences?.deals}
                      onChange={() => handlePreferenceChange('deals')}
                      className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">Deal Alerts</div>
                      <div className="text-sm text-gray-500">Price drops and exclusive offers</div>
                    </div>
                  </label>

                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences?.reviews}
                      onChange={() => handlePreferenceChange('reviews')}
                      className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">New Reviews</div>
                      <div className="text-sm text-gray-500">Latest product reviews and comparisons</div>
                    </div>
                  </label>

                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences?.guides}
                      onChange={() => handlePreferenceChange('guides')}
                      className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">Expert Guides</div>
                      <div className="text-sm text-gray-500">Buying guides and maintenance tips</div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                variant="default"
                size="lg"
                loading={isLoading}
                iconName="Mail"
                iconPosition="left"
                fullWidth
                disabled={!email || (!preferences?.deals && !preferences?.reviews && !preferences?.guides)}
              >
                {isLoading ? 'Subscribing...' : 'Subscribe Now'}
              </Button>

              {/* Privacy Note */}
              <p className="text-xs text-gray-500 text-center">
                By subscribing, you agree to our privacy policy. Unsubscribe at any time.
                We never share your email address.
              </p>
            </form>

            {/* Social Proof */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex items-center justify-center space-x-4 text-sm text-gray-500">
                <div className="flex items-center space-x-1">
                  <Icon name="Users" size={16} />
                  <span>500K+ subscribers</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Icon name="Star" size={16} className="text-yellow-500" />
                  <span>4.9/5 rating</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NewsletterSignup;